const API_BASE = "/api/v1"

function getToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem("auth_token")
}

function setToken(token: string): void {
  localStorage.setItem("auth_token", token)
}

function clearToken(): void {
  localStorage.removeItem("auth_token")
  localStorage.removeItem("auth_user")
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    clearToken()
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login"
    }
    throw new Error("Unauthorized")
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),

  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  delete: <T>(path: string) =>
    apiFetch<T>(path, {
      method: "DELETE",
    }),
}

export async function login(email: string, password: string) {
  const data = await api.post<{ token: string; user: import("./types").AuthUser; mfa_required?: boolean; user_id?: number }>(
    "/auth/login",
    { email, password }
  )

  // MFA required - don't set token yet
  if (data.mfa_required) {
    return data
  }

  setToken(data.token)
  localStorage.setItem("auth_user", JSON.stringify(data.user))
  return data
}

export async function verifyMfa(userId: number, code: string) {
  const data = await api.post<{ token: string; user: import("./types").AuthUser }>(
    "/auth/mfa/verify",
    { user_id: userId, code }
  )
  setToken(data.token)
  localStorage.setItem("auth_user", JSON.stringify(data.user))
  return data
}

export async function downloadFile(path: string, filename: string) {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, { headers })

  if (!response.ok) {
    throw new Error(`Download failed: HTTP ${response.status}`)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  a.remove()
}

export function logout() {
  clearToken()
  if (typeof window !== "undefined") {
    window.location.href = "/login"
  }
}

export function getStoredUser(): import("./types").AuthUser | null {
  if (typeof window === "undefined") return null
  const raw = localStorage.getItem("auth_user")
  return raw ? JSON.parse(raw) : null
}

export function isAuthenticated(): boolean {
  return !!getToken()
}
